import { type Address, zeroAddress } from 'viem'
import { Aggregator, Call } from './interfaces.js'
import { zeroExWrapperUrl } from 'abis/set/sets.js'

type QuoteResponse = {
  sellAmount: string
  buyAmount: string
  transaction: Call & {
    gas: string
  }
}

export class ZeroExAggregator implements Aggregator {
  private lastCallTimestamp = 0

  constructor(
    private readonly baseUrl: string,
    private readonly maxRetries: number,
    private readonly slippageBps: number,
    private readonly shouldSellEntireBalance: boolean,
  ) {}

  static get builder() {
    return new ZeroExAggregatorBuilder()
  }

  async quote(
    chainId: number,
    base: Address,
    quote: Address,
    input: bigint,
    takerAddress: Address,
  ) {
    const sellToken = this.remapNativeAddress(base)
    const buyToken = this.remapNativeAddress(quote)

    const baseUrl = `${this.baseUrl}`
    const takerAddressStr = `&taker=${takerAddress}`
    const url = `${baseUrl}/swap/allowance-holder/quote?chainId=${chainId}&sellToken=${sellToken}&buyToken=${buyToken}&sellAmount=${input}&slippageBps=${this.slippageBps}&sellEntireBalance=${this.shouldSellEntireBalance}${takerAddressStr}`

    const json = await this.fetchWithRetries<QuoteResponse>(url, this.maxRetries)

    return {
      to: json.transaction.to,
      data: json.transaction.data,
      value: BigInt(json.transaction.value),
      output: BigInt(json.buyAmount),
      gas: BigInt(json.transaction.gas ?? 0),
      allowanceTarget: json.transaction.to,
    }
  }

  private async fetchWithRetries<T>(url: string, maxRetries: number): Promise<T> {
    let retries = 0
    let json: T | undefined = undefined
    while (retries < maxRetries) {
      try {
        json = await this.fetch<T>(url)
        break
      } catch (e) {
        retries++
      }
    }

    if (json === undefined) throw new Error(`Failed to fetch quote after ${maxRetries} retries`)

    return json
  }

  private async fetch<T>(url: string): Promise<T> {
    const now = Date.now()
    if (this.lastCallTimestamp !== 0 && now - this.lastCallTimestamp < 1000) {
      await sleep(1000)
    }

    this.lastCallTimestamp = now + 1000

    return await fetch(url).then(async (res) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch quote: ${res.statusText}`)
      }
      return (await res.json()) as T
    })
  }

  private remapNativeAddress(address: Address): Address {
    return address === zeroAddress ? '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' : address
  }
}

class ZeroExAggregatorBuilder {
  private baseUrl = zeroExWrapperUrl
  private maxRetries = 3
  private slippageBps = 100
  private shouldSellEntireBalance = true

  withBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl
    return this
  }

  withMaxRetries(maxRetries: number) {
    this.maxRetries = maxRetries
    return this
  }

  withSlippagePercentage(slippageBps: number) {
    this.slippageBps = slippageBps
    return this
  }

  withShouldSellEntireBalance(shouldSellEntireBalance: boolean) {
    this.shouldSellEntireBalance = shouldSellEntireBalance
    return this
  }

  build() {
    return new ZeroExAggregator(
      this.baseUrl,
      this.maxRetries,
      this.slippageBps,
      this.shouldSellEntireBalance,
    )
  }
}

/**
 * Delays the execution of the code for a specified number of milliseconds.
 *
 * @param ms - The number of milliseconds to delay
 * @returns A Promise that resolves after the specified delay
 */
export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))
